import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import get from 'lodash/get';

/**
 * These functions help abstract the usage of different additional interest fields for CP vs. HO.
 * See usage examples below.
 * @returns {{ho: {getPolicyContactDetailAIByType: (function(*=, [*, *]): {additionalInterestContact: {}, additionalInterestDetail: {}})}, cp: {getPolicyContactDetailAIByType: (function(*=, [*, *]): {additionalInterestContact: {}, additionalInterestDetail: {}})}}}
 *
 * @Example
 * Use the appropriate namespace for lob separation.
 * import TWIAPolicyContactRoleHelper from 'twia-portals-util-js'
 *      TWIAPolicyContactRoleHelper.ho.policyAdditionalInterestAndContactDetailByPaymentMethod(paymentMethod)(PolicyContactDetailsDTO)
 *      TWIAPolicyContactRoleHelper.cp.policyAdditionalInterestAndContactDetailByPaymentMethod(paymentMethod)(PolicyContactDetailsDTO)
 *      TWIAPolicyContactRoleHelper.policyAdditionalInterests(PolicyContactDetailsDTO)
 * @constructor
 */

/**
 * This map provides the flexibility to determine the search-criteria fields, based on LOB.
 * @type {{Homeowners: {alt_payor: [string, string], mortgagee_escrow: [string, string], prem_fina_other: [string, string], prem_fina_wellington: [string, string]}, CommercialProperty: {alt_payor: [string, string], mortgagee_escrow: [string, string], prem_fina_other: [string, string], prem_fina_wellington: [string, string]}}}
 */
const LOB_PAYMENTMETHOD_MAP = {
    'Homeowners': {
        'mortgagee_escrow': ['mortgagePosition_TWIA', 'FirstMortgagee'],
/* US20716
        'alt_payor': ['type', 'ALTERNATEPAYOR_EXT'],
        'prem_fina_wellington': ['premiumFinanceCompanyType_TWIA', 'wellingtonprmfin'],
 */
        'prem_fina_other': ['premiumFinanceCompanyType_TWIA', 'other']
    },
    'CommercialProperty': {
        'mortgagee_escrow': ['type', 'MORTGAGEE'],
/* US20716
        'alt_payor': ['type', 'ALTERNATEPAYOR_EXT'],
        'prem_fina_wellington': ['premiumFinanceCompanyType_TWIA', 'wellingtonprmfin'],
 */
        'prem_fina_other': ['premiumFinanceCompanyType_TWIA', 'other']
    }
};

const TWIAPolicyContactRoleHelper = () => {

    const policyAdditionalInterests = policyContactDetails => filter(policyContactDetails, {
            policyContactRoles: [{
                subtype: 'PolicyAddlInterest',
            }]
        }) || {};

    // US12232 - Commercial Policy Change - PolicyDetails
    const policyPrimaryNamedInsured = policyContactDetails => filter(policyContactDetails, {
            policyContactRoles: [{
                subtype: 'PolicyPriNamedInsured',
            }]
        }) || {};

    // eslint-disable-next-line no-underscore-dangle
    const _getAdditionalInterestContactAndDetail = (property, policyContactDetails = {}) => {
        const [policyContactDetail] = policyContactDetails;
        let aid = {};
        
        if (!isEmpty(policyContactDetail)) {
            ({
                policyContactRoles: [{
                    policyAdditionalInterest: {
                        [property]: [aid = []]
                    } = {}
                }] = []
            } = policyContactDetail);
        }

        return {
            additionalInterestContact: policyContactDetail,
            additionalInterestDetail: aid
        };
    };

    const policyAdditionalInterest = parentKey => ([prop, val]) => (policyContactDetails) => filter(policyContactDetails, {
            policyContactRoles: [{
                subtype: 'PolicyAddlInterest',
                policyAdditionalInterest: {
                    [parentKey]: [{[prop]: val}]
                }
            }]
        }) || {};

    const getParentKey = (productCode) => {
        if (productCode === 'Homeowners') {
            return 'additionalInterestDetails';
        }
 
        if (productCode === 'CommercialProperty') {
            return 'cpAdditionalInterestDetails_TWIA';
        }

        return undefined;
    };

    const policyAdditionalInterestByPaymentMethod = productCode => paymentMethod => (policyContactDetails) => {
        const lobMap = get(LOB_PAYMENTMETHOD_MAP, productCode);
        const [prop, val] = get(lobMap, paymentMethod) || [];
        const parentKey = getParentKey(productCode);

        return policyAdditionalInterest(parentKey)([prop, val])(policyContactDetails);
    };

    const policyAdditionalInterestAndContactDetailByPaymentMethod = productCode => paymentMethod => (policyContactDetails) => {
        const found = policyAdditionalInterestByPaymentMethod(productCode)(paymentMethod)(policyContactDetails);
        const parentKey = getParentKey(productCode);

        return _getAdditionalInterestContactAndDetail(parentKey, found);
    };


    const policyAdditionalInterestAndContactDetailByPaymentMethodCP = policyAdditionalInterestAndContactDetailByPaymentMethod('CommercialProperty');
    const policyAdditionalInterestAndContactDetailByPaymentMethodHO = policyAdditionalInterestAndContactDetailByPaymentMethod('Homeowners');


    return {
        ho: {
            policyAdditionalInterestAndContactDetailByPaymentMethod: policyAdditionalInterestAndContactDetailByPaymentMethodHO,
            policyAdditionalInterests,
        },
        cp: {
            policyAdditionalInterestAndContactDetailByPaymentMethod: policyAdditionalInterestAndContactDetailByPaymentMethodCP,
            policyAdditionalInterests,
        },
        policyPrimaryNamedInsured,
        getAdditionalInterestKey: getParentKey
    };

};

export default TWIAPolicyContactRoleHelper();
